import { useState } from 'react'
import { Button, Col, Form, InputGroup } from 'react-bootstrap'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { formatISO } from 'date-fns/fp'
import { format, parseISO } from 'date-fns'
import { Icon } from '../icon'
import { faEye, faEyeSlash, faTrash } from '@fortawesome/free-solid-svg-icons'
import { ConfirmModal } from '../confirm'
import { useValidateTenantUrlSlugMutation } from '../../hooks'

const INITIAL_DATA = {
  name: '',
  urlSlug: '',
  dateCreated: formatISO(new Date()),
  hidden: false,
  description: ''
}

const validationSchema = yup.object({
  name: yup
    .string()
    .required('Portal Project Name is required')
    .max(255, 'Portal Project Name cannot be more than 255 characters'),
  urlSlug: yup
    .string()
    .required('Website URL is required')
    .matches(
      '^[a-zA-Z0-9\\-._~]+$',
      'Website URL can only have alphanumeric characters (a-z, A-Z, 0-9), hyphens (-), periods (.), underscores (_), and tildes (~) are allowed'
    )
    .max(255, 'Website URL cannot be more than 255 characters'),
  description: yup
    .string()
    .nullable()
    .max(255, 'Project Description cannot be more than 255 characters')
})

/** form to create or update the tenant */
const TenantForm = ({ formData = INITIAL_DATA, onSubmit, onDelete }) => {
  const MODAL = {
    DELETE_PROJECT: {
      title: `Are you sure you want to Delete this project "${formData.name}"?`,
      body:
        'Users affiliated with this project will no longer have access to all the content after the deletion, and the portal will be removed from your project list. This action cannot be undone unless by contacting the Portal Support team.'
    },
    HIDE_PROJECT: {
      title: `Are you sure you want to Hide this project "${formData.name}"?`,
      body:
        'Hiding this project will restrict all users from accessing it. Only Portal Admins can manage hidden projects.'
    },
    URL_ALERT: {
      alert: true,
      title: 'This Website URL is already taken',
      body:
        'The portal URL you entered is not available. Please choose a different URL and try again.'
    },
    URL_CHANGE: {
      title: `Are you sure you want to change the URL for this project "${formData.name}"?`,
      body: (
        <>
          <p className='font-weight-bold'>
            Changing your portal URL will affect existing links to files and
            documents within your portal project.
          </p>
          <p>
            This includes attachments in the Story Category and other content
            that references the old URL. After making this change, you will need
            to manually update any links or references to ensure they work
            correctly. This action cannot be undone automatically.
          </p>
          <p>
            If you have questions or need assistance, please contact our support
            team at{' '}
            <a href='mailto:portalsupport@osu.edu'>portalsupport@osu.edu</a>.
          </p>
        </>
      )
    }
  }
  const [modal, setModal] = useState({
    ...MODAL.DELETE_PROJECT,
    onConfirm: () => {}
  })
  const [showModal, setShowModal] = useState()
  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors, isDirty }
  } = useForm({
    defaultValues: formData,
    mode: 'onBlur',
    resolver: yupResolver(validationSchema)
  })
  const { mutate: validateUrlSlug } = useValidateTenantUrlSlugMutation()

  const onDiscard = () => {
    reset(formData)
  }

  const showHideProjectModal = (onChange) => {
    setModal({
      ...MODAL.HIDE_PROJECT,
      onConfirm: () => {
        setShowModal(false)
        onChange(true)
      }
    })
    setShowModal(true)
  }

  const checkDataAndSubmit = (data) => {
    if (data.urlSlug === formData.urlSlug) {
      onSubmit(data)
    } else {
      validateUrlSlug(
        { urlSlug: data.urlSlug },
        {
          onSuccess: ({ available }) => {
            if (!available) {
              setModal(MODAL.URL_ALERT)
              setShowModal(true)
            } else {
              setModal({
                ...MODAL.URL_CHANGE,
                onConfirm: () => {
                  setShowModal(false)
                  onSubmit(data)
                }
              })
              setShowModal(true)
            }
          }
        }
      )
    }
  }

  return (
    <>
      <Form onSubmit={handleSubmit((data) => checkDataAndSubmit(data))}>
        <Form.Row className='mb-2'>
          <Form.Group as={Col} lg={6} md={8} controlId='name'>
            <Form.Label className='font-weight-bold'>
              Portal Project Name
              <span className='text-secondary'> *</span>
            </Form.Label>
            <Form.Control
              required
              aria-required='true'
              type='text'
              {...register('name')}
              isInvalid={!!errors.name}
            />
            <Form.Control.Feedback type='invalid'>
              {errors.name?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Form.Row>
        <Form.Row className='mb-2'>
          <Form.Group as={Col} lg={6} md={8} controlId='urlSlug'>
            <Form.Label className='font-weight-bold'>
              Website URL
              <span className='text-secondary'> *</span>
            </Form.Label>
            <InputGroup>
              <InputGroup.Prepend>
                <InputGroup.Text>{window.location.host}/</InputGroup.Text>
              </InputGroup.Prepend>
              <Form.Control
                required
                aria-required='true'
                type='text'
                {...register('urlSlug')}
                isInvalid={!!errors.urlSlug}
              />
              <Form.Control.Feedback type='invalid'>
                {errors.urlSlug?.message}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Form.Row>
        <Form.Row className='mb-2'>
          <Col lg={6} md={8}>
            <p className='font-weight-bold mb-1'>Date Created</p>
            <p className='pl-2'>
              {format(parseISO(formData.dateCreated), 'MM/dd/yyyy')}
            </p>
          </Col>
        </Form.Row>
        <Form.Row className='mb-3'>
          <Col lg={6} md={8}>
            <p className='font-weight-bold mb-1'>Actions</p>
            <Controller
              control={control}
              name='hidden'
              render={({ field: { onChange, value } }) => (
                <Button
                  variant='link'
                  onClick={() =>
                    value ? onChange(false) : showHideProjectModal(onChange)
                  }
                  className='text-muted'
                >
                  <Icon icon={value ? faEye : faEyeSlash} size='lg' />
                  {value ? 'Unhide Project' : 'Hide Project'}
                </Button>
              )}
            />
            <Button
              variant='link'
              className='text-muted'
              onClick={() => {
                setModal({
                  ...MODAL.DELETE_PROJECT,
                  onConfirm: () => {
                    setShowModal(false)
                    onDelete(formData.id)
                  }
                })
                setShowModal(true)
              }}
            >
              <Icon icon={faTrash} size='lg' />
              Delete Project
            </Button>
          </Col>
        </Form.Row>
        <Form.Row className='mb-2'>
          <Form.Group as={Col} lg={6} md={8} controlId='description'>
            <Form.Label className='font-weight-bold'>
              Project Description
            </Form.Label>
            <Form.Control
              as='textarea'
              rows={4}
              {...register('description')}
              isInvalid={!!errors.description}
            />
            <Form.Control.Feedback type='invalid'>
              {errors.description?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Form.Row>
        <Form.Row>
          <Col className='d-flex justify-content-end'>
            <Button variant='secondary' type='submit' disabled={!isDirty}>
              Save Changes
            </Button>
            <Button variant='outline-dark' onClick={onDiscard} className='ml-3'>
              Discard
            </Button>
          </Col>
        </Form.Row>
      </Form>
      <ConfirmModal
        alert={modal.alert}
        show={showModal}
        title={modal.title}
        body={modal.body}
        onConfirm={modal.onConfirm}
        onCancel={() => setShowModal(false)}
      />
    </>
  )
}

export { TenantForm }
